/**
 * Flames Army — Wishlist
 * Pasos 4 y 5: la página + compartir por URL.
 *
 * Pide al servidor la card de cada producto guardado con la Section
 * Rendering API y la inserta en la grilla. Si la URL trae ?w=handle1,handle2
 * fusiona esos productos con la lista local antes de pintar.
 *
 * Solo corre en la página que tenga la sección de wishlist.
 */

(function () {
  'use strict';

  var page = document.querySelector('[data-wishlist-page]');
  if (!page || !window.Wishlist) return;

  var grid = page.querySelector('[data-wishlist-grid]');
  var loading = page.querySelector('[data-wishlist-loading]');
  var empty = page.querySelector('[data-wishlist-empty]');
  var actions = page.querySelector('[data-wishlist-actions]');
  var shareButton = page.querySelector('[data-wishlist-share]');
  var shareFeedback = page.querySelector('[data-wishlist-share-feedback]');

  var SHARE_PARAM = 'w';
  var MAX_SHARED = 50;
  // Un handle de Shopify solo puede tener minúsculas, números y guiones.
  var HANDLE_PATTERN = /^[a-z0-9-]+$/;

  // Cache de respuestas por handle: evita pedir la misma card dos veces
  // cuando un producto llega por el link compartido y además se pinta.
  var cardCache = {};

  /* ------------------------------------------------------------------
   * Helpers de visibilidad
   * ------------------------------------------------------------------ */

  function show(el) {
    if (el) el.removeAttribute('hidden');
  }

  function hide(el) {
    if (el) el.setAttribute('hidden', '');
  }

  function showEmpty() {
    hide(loading);
    hide(grid);
    hide(actions);
    show(empty);
  }

  function showGrid() {
    hide(loading);
    hide(empty);
    show(grid);
    show(actions);
  }

  /* ------------------------------------------------------------------
   * Traer cards
   * ------------------------------------------------------------------ */

  function fetchCard(handle) {
    if (!handle) return Promise.resolve(null);

    if (Object.prototype.hasOwnProperty.call(cardCache, handle)) {
      return Promise.resolve(cardCache[handle]);
    }

    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=wishlist-card')
      .then(function (response) {
        // 404 = producto borrado o despublicado desde que lo guardaron.
        if (!response.ok) return null;
        return response.text();
      })
      .then(function (html) {
        cardCache[handle] = html || null;
        return cardCache[handle];
      })
      .catch(function () {
        cardCache[handle] = null;
        return null;
      });
  }

  // El ID real del producto viene dentro del HTML que devuelve el servidor.
  function extractId(html) {
    if (!html) return null;

    var holder = document.createElement('div');
    holder.innerHTML = html;

    var card = holder.querySelector('[data-wishlist-card-id]');
    return card ? card.getAttribute('data-wishlist-card-id') : null;
  }

  /* ------------------------------------------------------------------
   * Link compartido
   * ------------------------------------------------------------------ */

  function readSharedHandles() {
    if (!window.URLSearchParams) return [];

    var raw = new URLSearchParams(window.location.search).get(SHARE_PARAM);
    if (!raw) return [];

    var handles = [];
    var parts = raw.split(',');

    for (var i = 0; i < parts.length && handles.length < MAX_SHARED; i++) {
      var handle = parts[i].trim().toLowerCase();
      // Descartamos cualquier cosa que no tenga forma de handle.
      if (handle && HANDLE_PATTERN.test(handle) && handles.indexOf(handle) === -1) {
        handles.push(handle);
      }
    }

    return handles;
  }

  function cleanUrl() {
    if (!window.history || !window.history.replaceState) return;

    var url = new URL(window.location.href);
    url.searchParams.delete(SHARE_PARAM);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }

  function alreadySaved(handle) {
    var items = window.Wishlist.get();

    for (var i = 0; i < items.length; i++) {
      if (items[i].handle === handle) return true;
    }

    return false;
  }

  // Trae las cards de los handles compartidos, saca su ID y los suma a la lista.
  function mergeShared() {
    var handles = readSharedHandles();
    if (handles.length === 0) return Promise.resolve();

    var pending = handles.filter(function (handle) {
      return !alreadySaved(handle);
    });

    if (pending.length === 0) {
      cleanUrl();
      return Promise.resolve();
    }

    show(loading);

    return Promise.all(
      pending.map(function (handle) {
        return fetchCard(handle).then(function (html) {
          var id = extractId(html);
          if (!id) return;
          window.Wishlist.add({ id: id, handle: handle });
        });
      })
    ).then(function () {
      cleanUrl();
    });
  }

  function buildShareUrl() {
    var handles = [];
    var items = window.Wishlist.get();

    for (var i = 0; i < items.length && handles.length < MAX_SHARED; i++) {
      if (items[i].handle) handles.push(items[i].handle);
    }

    if (handles.length === 0) return null;

    return (
      window.location.origin +
      window.location.pathname +
      '?' +
      SHARE_PARAM +
      '=' +
      handles.join(',')
    );
  }

  function flashFeedback() {
    if (!shareFeedback) return;

    show(shareFeedback);
    setTimeout(function () {
      hide(shareFeedback);
    }, 2500);
  }

  if (shareButton) {
    shareButton.addEventListener('click', function () {
      var url = buildShareUrl();
      if (!url) return;

      // En móvil abre el menú de compartir del sistema; en escritorio copia.
      if (navigator.share) {
        navigator.share({ title: document.title, url: url }).catch(function () {});
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(flashFeedback).catch(function () {});
        return;
      }

      window.prompt('Copiá este link para compartir tu Wishlist:', url);
    });
  }

  /* ------------------------------------------------------------------
   * Pintado
   * ------------------------------------------------------------------ */

  function render() {
    var items = window.Wishlist.get();

    if (items.length === 0) {
      showEmpty();
      return;
    }

    show(loading);

    Promise.all(
      items.map(function (item) {
        return fetchCard(item.handle).then(function (html) {
          return html ? { id: item.id, html: html } : null;
        });
      })
    ).then(function (results) {
      var fragment = document.createDocumentFragment();
      var missing = [];
      var rendered = 0;

      // Respetamos el orden de la lista: lo más reciente primero.
      for (var i = 0; i < results.length; i++) {
        var result = results[i];

        if (!result) {
          missing.push(items[i].id);
          continue;
        }

        var holder = document.createElement('div');
        holder.innerHTML = result.html;

        while (holder.firstChild) {
          fragment.appendChild(holder.firstChild);
        }

        rendered++;
      }

      // Limpiamos de la lista lo que ya no existe en la tienda.
      for (var j = 0; j < missing.length; j++) {
        window.Wishlist.remove(missing[j]);
      }

      grid.innerHTML = '';
      grid.appendChild(fragment);

      if (rendered === 0) {
        showEmpty();
      } else {
        showGrid();
      }
    });
  }

  /* ------------------------------------------------------------------
   * Quitar desde la propia página
   * ------------------------------------------------------------------ */

  document.addEventListener(window.Wishlist.EVENT, function (event) {
    var detail = event.detail || {};

    if (detail.action !== 'remove' || !detail.item) return;

    var card = grid.querySelector('[data-wishlist-card-id="' + detail.item.id + '"]');

    if (card) {
      // El item de la grilla envuelve a la card.
      var wrapper = card.closest('.resource-list__item') || card;
      wrapper.remove();
    }

    if (detail.count === 0) showEmpty();
  });

  /* ------------------------------------------------------------------
   * Arranque: primero fusionamos lo compartido, después pintamos.
   * ------------------------------------------------------------------ */

  mergeShared().then(render);
})();